import {AfterContentInit, AfterViewInit, Attribute, Component, Injector, OnDestroy, Optional} from "@angular/core";
import { IWidgetConfig, provideAsWidgetRef, Parent, register, StylableComponent } from '@wm/components-base';
import {CommonModule} from "@angular/common";
import {getEvaluatedData} from "../../../widgets/formWidgetUtils";
import {isArray, includes, isObject, isString, endsWith, isEmpty} from "lodash-es";
import {runtimeEventManager, setAttr} from "@wm/core";

const DEFAULT_CLS = 'app-tabbar app-top-nav';
const WIDGET_CONFIG: IWidgetConfig = {widgetType: 'wm-mobile-tabbar', hostClass: DEFAULT_CLS};
interface TabItem {
  icon: string;
  label: string;
  link?: string;
  value?: string;
  active?: boolean
}

@Component({
  selector: 'div[wmMobileTabbar]',
  template: `<nav class="navbar navbar-default">
    <ul class="tab-items nav navbar-nav">
      <li class="tab-item" *ngFor="let item of tabItems; index as i" [class.hidden]="!(tabItems.length === layout.max || i < layout.max)" >
        <a [class.active]="item.active" role="link" [attr.aria-label]="item.label" [attr.aria-current]="item.active ? 'page' : null">
          <i class="app-icon" [ngClass]="item.icon" aria-hidden="true"></i><label>{{item.label}}</label>
        </a>
      </li>
      <li class="menu-items dropdown" [class.hidden]="tabItems.length <= layout.max" [ngClass]="{dropup : position === bottom}">
        <a (click)="showMoreMenu = !showMoreMenu" href="javascript:void(0)" role="link"  aria-label="More options" [attr.aria-expanded]="showMoreMenu">
          <i class="app-icon {{morebuttoniconclass}}" aria-hidden="true"></i><label>{{morebuttonlabel}}</label>
        </a>
        <ul class="dropdown-menu dropdown-menu-right" *ngIf="showMoreMenu">
          <li role="menuitem" class="menu-item" *ngFor="let item of tabItems;index as i" [class.hidden]="i < layout.max">
            <a [ngClass]="{active : item.active}" class="dropdown-item" role="link"  [attr.aria-label]="item.label" [attr.aria-current]="item.active ? 'page' : null">
              <i class="app-icon" [ngClass]="item.icon" aria-hidden="true"></i><label>{{item.label}}</label>
            </a>
          </li>
        </ul>
      </li>
    </ul>
  </nav>`,
  providers: [
    provideAsWidgetRef(MobileTabbarComponent)
  ],
  imports: [
    CommonModule
  ],
  standalone: true,
  exportAs: 'wmMobileTabbar'
})
export class MobileTabbarComponent extends StylableComponent implements AfterViewInit, OnDestroy {
  static initializeProps = register('wm-mobile-tabbar');

  public itemlabel: string;
  public itemlink: string;
  public itemicon: string;

  public tabItems: Array<TabItem> = [];
  public layout: any = {};
  public position: any;
  public bottom: any;
  public morebuttoniconclass: any;
  public morebuttonlabel: any;
  public showMoreMenu: boolean = true;
  public variableService:any;

  private readonly _layouts = [
    {minwidth: 2048, max: 12},
    {minwidth: 1024, max: 10},
    {minwidth: 768, max: 7},
    {minwidth: 480, max: 5},
    {minwidth: 0, max: 4}
  ];

  constructor(
              inj: Injector,
              @Optional() viewParent: Parent,
              @Attribute('itemlabel.bind') public binditemlabel: string,
              @Attribute('itemicon.bind') public binditemicon: string,
              @Attribute('itemlink.bind') public binditemlink: string) {
    super(inj, WIDGET_CONFIG, viewParent);
    this.variableService = runtimeEventManager.getService('variableService');
    let dataset = this.$attrs.get('dataset.bind');
    if(dataset){
      let variableObj= this.variableService.getVariableByName(dataset.split('.')[1]);
      if(variableObj && variableObj.propertiesMap) {
        this.tabItems = this.getTabItems(variableObj.propertiesMap.columns);
      } else if (variableObj && !isEmpty(variableObj.dataSet)) {
        this.tabItems = this.getTabItems(variableObj.dataSet);
      } else if (dataset.startsWith('[') && dataset.endsWith(']')) {
        let parsed;
        try {
          parsed = JSON.parse(dataset);
        } catch (e) {
          try {
            parsed = eval('(' + dataset + ')');
          } catch (evalError) {
            this.tabItems = this.getDefaultItems();
            return;
          }
        }
        this.tabItems = this.getTabItems(parsed);
      } else {
        this.tabItems = this.getDefaultItems();
      }
    }else {
      this.tabItems = this.getDefaultItems();
    }
  }

  public override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'dataset') {
      if (nv) {
        let variableObj= this.variableService.getVariableInstance(nv, "");
        let variables = variableObj.propertiesMap.columns
        if(nv && nv.endsWith("dataSet")){
            this.tabItems = this.getTabItems(variables);
        }else {
          const values = variables.map((items: any) => items[nv.split('.')[nv.split('.').length - 1]]);
          this.tabItems = this.getTabItems(values);
        }
      } else {
        this.tabItems = this.getDefaultItems();
      }
      runtimeEventManager.emit('updatePropPanelOptions',this);
    } else {
      super.onPropertyChange(key, nv, ov);
    }
  }

  private getDefaultItems() {
    return [{
      'label' : 'Home',
      'icon'  : 'wm-sl-r sl-home'
    },{
      'label' : 'Analytics',
      'icon'  : 'wm-sl-r sl-graph-ascend'
    },{
      'label' : 'Alerts',
      'icon'  : 'wm-sl-r sl-alarm-bell'
    },{
      'label' : 'Settings',
      'icon'  : 'wm-sl-r sl-settings'
    }];
  }

  private getItems(itemArray: any[]): TabItem[] {
    return itemArray.map(item => ({
      label: item,
      icon: 'wi wi-' + item
    }));
  }

  private getTabItems(value: any): TabItem[] {
    if (isArray(value)) {
      if (isObject(value[0])) {
        return (value as any[]).map(item => {
          const link = getEvaluatedData(item, {field: this.itemlink, 'bindExpression': this.binditemlink}, this.viewParent) || item.link;
          const activePageName = window.location.hash.substr(2);
          return {
            label: getEvaluatedData(item, {field: this.itemlabel, bindExpression: this.binditemlabel}, this.viewParent) || item.label,
            icon: getEvaluatedData(item, {field: this.itemicon, bindExpression: this.binditemicon}, this.viewParent) || item.icon,
            link: link,
            active: includes([activePageName, '#' + activePageName, '#/' + activePageName], link)
          };
        });
      } else {
        return this.getItems(value as any[]);
      }
    } else if (isString(value)) {
      return this.getItems((value as string).split(','));
    }
    return [];
  }

  ngAfterContentInit() {
    this.layout = this.getSuitableLayout() || this._layouts[0];
  }

  private getSuitableLayout() {
    const avaiableWidth: number | undefined = $(this.nativeElement).parent().width();
    return avaiableWidth ? this._layouts.find(l => avaiableWidth >= l.minwidth) : this._layouts[0];
  }
}
